import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useBodyScrollLock } from '@/hooks/use-body-scroll-lock';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Upload, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { notify } from '@/utils/notifications';
import { setWallpapers, subscribeManyWallpapers, WallpapersResponse } from '@/lib/collections/wallpapers';
import { Time, Address } from '@/lib/db-client';
import { setFile, getFiles, FileItem } from '@/lib/db-client';
import { useAuth } from '@pooflabs/web';
import { useRealtimeData } from '@/hooks/use-realtime-data';
import {
  ContentType,
  CONTENT_TYPE_BADGE,
  ALL_ACCEPT_ATTR,
  ALL_FORMAT_HINT,
  MEDIA_TYPE_MAP,
  BASE_LIVE_CATEGORIES,
  RINGTONE_CATEGORIES,
  detectContentType,
} from '@/utils/contentTypeDetect';
import { useWallpaperCategoriesFromDB } from '@/utils/wallpaperCategories';
import { SKR_MIN_PRICE_RAW } from '@/lib/constants';
import { processWallpaperImage } from '@/utils/processWallpaperImage';

export type { ContentType };
export { RINGTONE_CATEGORIES };
export type RingtoneCategory = typeof RINGTONE_CATEGORIES[number];

interface UserUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const UserUploadModal: React.FC<UserUploadModalProps> = ({ isOpen, onClose }) => {
  // Lock body scroll while modal is open
  useBodyScrollLock(isOpen);
  const { user } = useAuth();
  const [detectedType, setDetectedType] = useState<ContentType | null>(null);
  const [category, setCategory] = useState<string>('');
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState<number>(1);
  const [file, setFile_] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Subscribe to all published wallpapers to derive live category lists for live-wallpaper type
  const { data: allWallpapers } = useRealtimeData<WallpapersResponse[]>(
    subscribeManyWallpapers,
    true,
    "where status = 'published'"
  );

  // Wallpaper categories come exclusively from the admin-managed DB list.
  const { categories: wallpaperCategories } = useWallpaperCategoriesFromDB();

  const liveCategories = useMemo(() => {
    const fromData = (allWallpapers ?? [])
      .filter(w => w.mediaType === 'live-wallpaper')
      .map(w => w.category)
      .filter((c): c is string => !!c && c.trim() !== '');
    const unique = [...new Set([...BASE_LIVE_CATEGORIES, ...fromData])].filter(c => c.toLowerCase() !== 'all');
    return unique;
  }, [allWallpapers]);

  const ringtoneCategories = useMemo(() => {
    // Always show all 8 fixed categories regardless of what's in the DB
    return [...RINGTONE_CATEGORIES];
  }, []);

  const categoriesForType: Record<ContentType, string[]> = useMemo(() => ({
    wallpaper: wallpaperCategories,
    live: liveCategories,
    ringtone: ringtoneCategories,
  }), [wallpaperCategories, liveCategories, ringtoneCategories]);

  const currentCategories = detectedType ? categoriesForType[detectedType] : [];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;

    const detected = detectContentType(f);
    if (!detected) {
      notify.error('Unsupported file format. Upload JPG/PNG/WEBP, GIF/MP4/MOV, or MP3/WAV/OGG/AAC/M4A.', user?.address);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // If switching content type, reset category
    if (detected !== detectedType) {
      setCategory('');
    }

    setFile_(f);
    setDetectedType(detected);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    if (submitting) return;
    setDetectedType(null);
    setCategory('');
    setTitle('');
    setPrice(1);
    setFile_(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!user?.address) { notify.error('Please connect your wallet first', user?.address); return; }
    if (!title.trim()) { notify.error('Title is required', user?.address); return; }
    if (title.trim().length > 100) { notify.error('Title must be under 100 characters', user?.address); return; }
    if (!price || price < 1) { notify.error('Price must be at least 1 SKR', user?.address); return; }
    if (!file) { notify.error('Please select a file', user?.address); return; }
    if (!detectedType) { notify.error('Could not detect file type. Please select a valid file.', user?.address); return; }
    if (!category) { notify.error('Please select a category', user?.address); return; }

    setSubmitting(true);
    setUploading(true);

    try {
      // 1. Normalize wallpaper images to the fixed 1400x3100 cover-crop, once, then upload
      const processedFile = await processWallpaperImage(file);
      const sanitizedName = processedFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const uploadPath = `appFiles/${Date.now()}-${sanitizedName}`;
      const uploaded = await setFile(uploadPath, processedFile);
      if (!uploaded) {
        notify.error('File upload failed. Please try again.', user?.address);
        return;
      }

      setUploading(false);

      // 2. Get the public URL
      const filesResult = await getFiles(uploadPath) as FileItem[] | null;
      const fileRecord = Array.isArray(filesResult) ? filesResult[0] : null;
      const fileUrl = fileRecord?.url ?? '';
      if (!fileUrl) {
        notify.error('Upload succeeded but URL was not returned. Please try again.', user?.address);
        return;
      }

      // 3. Create the wallpaper record with status: pending
      const wallpaperId = `wall-user-${crypto.randomUUID()}`;
      const ok = await setWallpapers(wallpaperId, {
        title: title.trim(),
        imageUrl: fileUrl,
        thumbnailUrl: fileUrl,
        category: category || undefined,
        uploadedBy: Address.publicKey(user.address),
        createdAt: Time.Now,
        categorySetAt: Time.Now,
        downloadCount: 0,
        price: Math.floor(price * Number(SKR_MIN_PRICE_RAW)),
        mediaType: MEDIA_TYPE_MAP[detectedType],
        status: 'pending',
        sizeBytes: processedFile.size,
      } as any);

      if (ok) {
        handleClose();
      } else {
        notify.error('Permission denied. Make sure your wallet is connected correctly.', user?.address);
      }
    } catch (err) {
      console.error('User upload error:', err);
      notify.error(err instanceof Error ? err.message : 'Upload failed. Please try again.', user?.address);
    } finally {
      setSubmitting(false);
      setUploading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            position: 'fixed', inset: 0, zIndex: 5000,
            background: 'rgba(0,0,0,0.78)',
            backdropFilter: 'blur(14px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: 'spring', stiffness: 380, damping: 30 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 460,
              background: '#0c0b18',
              borderRadius: 20,
              border: '1px solid rgba(167,139,250,0.18)',
              boxShadow: '0 24px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(167,139,250,0.06)',
              overflow: 'hidden',
              maxHeight: 'calc(100vh - 32px)',
              overflowY: 'auto',
            }}
          >
            {/* Header */}
            <div style={{
              padding: '18px 20px 0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <h3 style={{
                  fontSize: 17, fontWeight: 800, color: '#f0f0f5', margin: 0,
                  fontFamily: "'Syne', sans-serif", letterSpacing: '-0.02em',
                }}>
                  Upload Content
                </h3>
                <p style={{
                  fontSize: 12, color: 'rgba(167,139,250,0.6)', margin: '2px 0 0',
                  fontFamily: "'DM Mono', monospace",
                }}>
                  Submit for review
                </p>
              </div>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={handleClose}
                disabled={submitting}
                style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', color: 'rgba(240,240,245,0.5)',
                }}
              >
                <X size={15} />
              </motion.button>
            </div>

            <div style={{ padding: '16px 20px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* File picker */}
              <div>
                <label style={{
                  display: 'block', fontSize: 10, fontWeight: 700,
                  color: 'rgba(167,139,250,0.6)', textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginBottom: 4,
                  fontFamily: "'Syne', sans-serif",
                }}>
                  File
                </label>
                {/* Format hint */}
                <div style={{
                  fontSize: 10, color: 'rgba(167,139,250,0.45)',
                  marginBottom: 6,
                  fontFamily: "'DM Mono', monospace",
                  lineHeight: 1.5,
                }}>
                  {ALL_FORMAT_HINT}
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ALL_ACCEPT_ATTR}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <motion.button
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%', padding: '12px 14px',
                    borderRadius: 10,
                    background: file ? 'rgba(139,92,246,0.1)' : 'rgba(255,255,255,0.04)',
                    border: file ? '1px solid rgba(167,139,250,0.4)' : '1px dashed rgba(255,255,255,0.15)',
                    color: file ? '#a78bfa' : 'rgba(240,240,245,0.4)',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 10,
                    transition: 'all 0.14s ease',
                    textAlign: 'left',
                  }}
                >
                  <Upload size={16} style={{ flexShrink: 0 }} />
                  <span style={{
                    fontSize: 12, fontFamily: "'DM Mono', monospace",
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {file ? file.name : 'Choose file…'}
                  </span>
                  {file && (
                    <Check size={14} style={{ flexShrink: 0, marginLeft: 'auto', color: '#4ade80' }} />
                  )}
                </motion.button>

                {/* Auto-detected type badge */}
                <AnimatePresence>
                  {detectedType && (
                    <motion.div
                      key={detectedType}
                      initial={{ opacity: 0, y: -4, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.95 }}
                      transition={{ duration: 0.16 }}
                      style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}
                    >
                      <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '4px 10px',
                        borderRadius: 20,
                        background: 'rgba(139,92,246,0.15)',
                        border: '1px solid rgba(167,139,250,0.35)',
                        color: '#c4b5fd',
                        fontSize: 11, fontWeight: 700,
                        fontFamily: "'Syne', sans-serif",
                        letterSpacing: '0.04em',
                        userSelect: 'none',
                        cursor: 'default',
                      }}>
                        <span style={{ fontSize: 13 }}>{CONTENT_TYPE_BADGE[detectedType].emoji}</span>
                        {CONTENT_TYPE_BADGE[detectedType].label}
                      </div>
                      <span style={{
                        fontSize: 10, color: 'rgba(167,139,250,0.45)',
                        fontFamily: "'DM Mono', monospace",
                      }}>
                        auto-detected
                      </span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Category selector — only shown after file is selected */}
              {detectedType && (
                <div>
                  <label style={{
                    display: 'block', fontSize: 10, fontWeight: 700,
                    color: 'rgba(167,139,250,0.6)', textTransform: 'uppercase',
                    letterSpacing: '0.08em', marginBottom: 7,
                    fontFamily: "'Syne', sans-serif",
                  }}>
                    Category
                  </label>
                  {currentCategories.length === 0 ? (
                    <div style={{
                      fontSize: 12, color: 'rgba(240,240,245,0.3)',
                      fontFamily: "'DM Mono', monospace",
                      padding: '6px 0',
                    }}>
                      No categories yet for this type — will be assigned on review.
                    </div>
                  ) : (
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={detectedType}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.14 }}
                        style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}
                      >
                        {currentCategories.map(cat => {
                          const active = category === cat;
                          return (
                            <motion.button
                              key={cat}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setCategory(active ? '' : cat)}
                              style={{
                                padding: '5px 12px',
                                borderRadius: 20,
                                background: active ? 'rgba(139,92,246,0.22)' : 'rgba(255,255,255,0.04)',
                                border: active ? '1px solid rgba(167,139,250,0.5)' : '1px solid rgba(255,255,255,0.08)',
                                color: active ? '#a78bfa' : 'rgba(240,240,245,0.5)',
                                fontSize: 12, fontWeight: 600,
                                cursor: 'pointer',
                                fontFamily: "'Syne', sans-serif",
                                transition: 'all 0.14s ease',
                              }}
                            >
                              {cat}
                            </motion.button>
                          );
                        })}
                      </motion.div>
                    </AnimatePresence>
                  )}
                </div>
              )}

              {/* Title */}
              <div>
                <label style={{
                  display: 'block', fontSize: 10, fontWeight: 700,
                  color: 'rgba(167,139,250,0.6)', textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginBottom: 7,
                  fontFamily: "'Syne', sans-serif",
                }}>
                  Title *
                </label>
                <input
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  maxLength={100}
                  placeholder="Enter a title…"
                  style={{
                    width: '100%', padding: '10px 12px',
                    borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.04)', color: '#f0f0f5',
                    fontSize: 13, outline: 'none', boxSizing: 'border-box',
                    fontFamily: "'Syne', sans-serif",
                    transition: 'border-color 0.14s ease',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.4)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
                <div style={{
                  fontSize: 10, color: 'rgba(240,240,245,0.25)',
                  textAlign: 'right', marginTop: 3,
                  fontFamily: "'DM Mono', monospace",
                }}>
                  {title.length}/100
                </div>
              </div>

              {/* Price */}
              <div>
                <label style={{
                  display: 'block', fontSize: 10, fontWeight: 700,
                  color: 'rgba(167,139,250,0.6)', textTransform: 'uppercase',
                  letterSpacing: '0.08em', marginBottom: 7,
                  fontFamily: "'Syne', sans-serif",
                }}>
                  Price (SKR) *
                </label>
                <input
                  type="number"
                  min={1}
                  value={price}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    setPrice(isNaN(val) ? 1 : val);
                  }}
                  required
                  style={{
                    width: '100%', padding: '10px 12px',
                    borderRadius: 10, border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.04)', color: '#f0f0f5',
                    fontSize: 13, outline: 'none', boxSizing: 'border-box',
                    fontFamily: "'Syne', sans-serif",
                    transition: 'border-color 0.14s ease',
                  }}
                  onFocus={e => { e.currentTarget.style.borderColor = 'rgba(167,139,250,0.4)'; }}
                  onBlur={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                />
                <div style={{
                  fontSize: 10, color: 'rgba(240,240,245,0.25)',
                  marginTop: 3,
                  fontFamily: "'DM Mono', monospace",
                }}>
                  Minimum 1 SKR
                </div>
              </div>

              {/* Submit */}
              <motion.button
                whileHover={submitting ? {} : { scale: 1.01, boxShadow: '0 0 20px rgba(139,92,246,0.4)' }}
                whileTap={submitting ? {} : { scale: 0.99 }}
                onClick={handleSubmit}
                disabled={submitting}
                style={{
                  width: '100%', padding: '12px 16px',
                  borderRadius: 12,
                  background: submitting ? 'rgba(139,92,246,0.4)' : 'rgba(139,92,246,0.85)',
                  border: '1px solid rgba(167,139,250,0.45)',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontFamily: "'Syne', sans-serif",
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  opacity: submitting ? 0.8 : 1,
                  transition: 'all 0.15s ease',
                }}
              >
                {submitting ? (
                  <>
                    <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
                    {uploading ? 'Uploading…' : 'Submitting…'}
                  </>
                ) : (
                  <>
                    <Upload size={15} />
                    Submit for Review
                  </>
                )}
              </motion.button>

              {/* Review notice */}
              <p style={{
                fontSize: 11, color: 'rgba(240,240,245,0.35)',
                margin: 0, textAlign: 'center', lineHeight: 1.5,
                fontFamily: "'Syne', sans-serif",
              }}>
                Your content will be reviewed before publishing. Usually takes up to 24 hours.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
